import React, {useEffect, useState} from 'react';
import {Alert, Button, Form, Input, message} from 'antd';
import {LockOutlined, MailOutlined, PlusOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import {useNavigate} from 'react-router-dom';
import {COMMON_API} from '../config/api';
import {encryptPassword} from '../security/passwordTransport';
import '../auth.css';

const RegisterIndexUi = () => {
    document.title = '注册';

    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const onFinish = async (values: any) => {
        if (values.admin_password !== values.user_password) {
            message.error('两次输入的密码不一致');
            return;
        }

        let encryptedPassword='';
        let encryptedConfirmation='';
        try {
            encryptedPassword=encryptPassword(values.admin_password);
            encryptedConfirmation=encryptPassword(values.user_password);
        } catch {
            message.error('密码加密失败，请刷新页面后重试');
            return;
        }
        const profileValues={...values};
        delete profileValues.admin_password;
        delete profileValues.user_password;
        const requestBody = new FormData();
        requestBody.append('finish_data', JSON.stringify(profileValues));
        requestBody.append('password_payload', encryptedPassword);
        requestBody.append('password_confirm_payload', encryptedConfirmation);
        requestBody.append('time', String(dayjs().unix()));
        requestBody.append('action', 'insert_node');

        setSubmitting(true);
        try {
            const response = await fetch(COMMON_API.register, {
                method: 'POST',
                body: requestBody,
            });
            const returnData = await response.json();

            switch (returnData.message) {
                case 2000:
                    message.success(returnData.value || '注册成功');
                    form.resetFields();
                    navigate('/login');
                    break;
                case 2001:
                    message.error(returnData.value);
                    break;
                case 4018:
                case 4019:
                    message.error(returnData.value);
                    navigate('/login?return_url=/register');
                    break;
                default:
                    message.error(returnData.value || '注册失败，请稍后再试');
                    break;
            }
        } catch (error) {
            message.error('注册请求失败，请检查网络或接口配置');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main className="auth-page">
            <section className="auth-visual">
                <div className="auth-brand">
                    <span className="auth-brand-mark">C2</span>
                    <span>ChatV2</span>
                </div>
                <div className="auth-copy">
                    <h1>创建管理员账号</h1>
                    <p>使用邮箱完成账号注册，后续可用于找回、通知和团队身份确认。</p>
                </div>
                {screenWidth > 700 && (
                    <div className="auth-stats">
                        <div>
                            <strong>Email</strong>
                            <span>必填注册</span>
                        </div>
                        <div>
                            <strong>Admin</strong>
                            <span>默认权限</span>
                        </div>
                    </div>
                )}
            </section>

            <section className="auth-panel" aria-label="注册表单">
                <div className="auth-card">
                    <div className="auth-card-header">
                        <span className="auth-kicker">Create account</span>
                        <h2>注册账号</h2>
                    </div>

                    <Form
                        layout="vertical"
                        form={form}
                        onFinish={onFinish}
                        className="auth-form"
                    >
                        <Form.Item
                            label="邮箱"
                            name="email"
                            rules={[
                                {required: true, message: '请输入邮箱'},
                                {type: 'email', message: '请输入正确的邮箱格式'},
                            ]}
                        >
                            <Input prefix={<MailOutlined />} placeholder="请输入邮箱" autoComplete="email" />
                        </Form.Item>

                        <Form.Item
                            label="密码"
                            name="admin_password"
                            rules={[{required: true, message: '请输入密码'},{min:8,message:'密码至少需要 8 位'}]}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder="请输入密码" autoComplete="new-password" />
                        </Form.Item>

                        <Form.Item
                            label="确认密码"
                            name="user_password"
                            dependencies={['admin_password']}
                            rules={[
                                {required: true, message: '请再次输入密码'},
                                ({getFieldValue}) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('admin_password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('两次输入的密码不一致'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder="请再次输入密码" autoComplete="new-password" />
                        </Form.Item>

                        <div className="auth-row auth-row-end">
                            <Button type="link" onClick={() => navigate('/login')} className="auth-link">
                                去登录
                            </Button>
                        </div>

                        <Form.Item className="auth-submit">
                            <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={submitting} block>
                                注册
                            </Button>
                        </Form.Item>
                        <Alert className="auth-security-tip" type="info" showIcon message="密码加密传输，并以不可逆哈希保存" />
                    </Form>
                </div>
            </section>
        </main>
    );
};

export default RegisterIndexUi;
